import React from 'react';
import EllipsisTextBubble from '@gp/components/EllipsisTextBubble';
import { GrossRevenueDataType } from '../types/services';
import { goToStudioDetailsPage } from '@gp/utils/studio';
import { CONSTANT_TEXTS } from '@gp/constants/const';

const getActualTdOpt = (
  valueKey: string,
  yoyKey: string,
  momKey: string,
  isSelectYoy: boolean,
  isSelectMom: boolean,
) => {
  let tendencyOpt: any = null;
  if (isSelectYoy && isSelectMom) {
    tendencyOpt = {
      type: 'tendency',
      value: yoyKey,
      style: { marginLeft: 10 },
      isTendencyLabel: true,
      label: 'YoY',
      isRenderDoubleTendency: true,
      doubleTendencyValue: momKey,
      doubleLabel: 'MoM',
    };
  } else if (isSelectYoy) {
    tendencyOpt = {
      type: 'tendency',
      value: yoyKey,
      style: { marginLeft: 10 },
      isTendencyLabel: true,
      label: 'YoY',
    };
  } else if (isSelectMom) {
    tendencyOpt = {
      type: 'tendency',
      value: momKey,
      style: { marginLeft: 10 },
      isTendencyLabel: true,
      label: 'MoM',
    };
  }

  return {
    number: {
      options: [
        {
          type: 'value',
          value: valueKey,
          isTitle: true,
          isFormat: true,
          style: { maxWidth: 70 },
        },
        tendencyOpt,
      ],
    },
  };
};

const getTableRecords = (loading: boolean, data: GrossRevenueDataType) => {
  if (loading) return [];
  const list = data?.list;
  const records = [];
  if (Array.isArray(list)) {
    const defaultFields: { [keyName: string]: any } = {}; // 默认字段组织
    if (list?.length && Array.isArray(list?.[0]?.details) && list?.[0]?.details?.length) {
      const details = list?.[0]?.details; // 后端返回的是降序的
      for (let i = 0, l = details?.length; i < l; i++) {
        const t = details?.[i]?.metric;
        defaultFields[t] = null;
        defaultFields[`${t}_yoy`] = null;
        defaultFields[`${t}_mom`] = null;
      }
    }

    let allStudioItem = null;
    for (let i = 0, l = list?.length; i < l; i++) {
      const t = list?.[i];
      const item: { [keyName: string]: string | number | undefined | null } = {
        uniqueId: `${t?.studio_id}_${i}${1}`,
        cover: t?.cover,
        game_name: t?.game_name,
        studio_id: t?.studio_id,
        studio_name: t?.studio_name,
        bo: t?.bo,
        genre: t?.genre,
        sub_genre: t?.sub_genre,
        platform: Array.isArray(t?.platform) ? t?.platform?.join(',') : t?.platform,
        ...defaultFields,
      };
      const details = t?.details;
      if (Array.isArray(details)) {
        for (let j = 0, l = details?.length; j < l; j++) {
          const k = details?.[j];
          const key = k?.metric;
          if (key) {
            item[key] = k?.value;
            item[`${key}_yoy`] = k?.yoy;
            item[`${key}_mom`] = k?.mom;
          }
        }
      }
      if (item?.studio_name === 'All Studios') {
        allStudioItem = item;
        continue;
      }
      records.push(item);
    }
    if (allStudioItem) {
      records.unshift(allStudioItem);
    }
  }

  return records;
};

const getTableOptions: (
  module: 'studio' | 'project',
  loading: boolean,
  data: GrossRevenueDataType,
  yoy: boolean,
  mom: boolean,
) => any = (module, loading, data, yoy, mom) => {
  // const dateTime = loading ? '-' : data?.date_time || '-';

  const studioColopt = [
    {
      keyName: 'studio',
      title: 'Studio',
      isHasDoubleTitle: true,
      width: 240,
      align: 'left',
      fixed: 'left',
      tdOpt: {
        project: {
          domain: 'studio',
          src: 'cover',
          title: 'studio_name',
          platform: 'platform',
          toDetails: (cvm: Record<string, any>, e: React.SyntheticEvent<HTMLElement>, isJump: boolean) => {
            e?.stopPropagation();
            if (!isJump) return;
            goToStudioDetailsPage({ id: cvm?.studio_id, name: cvm?.studio_name });
          },
        },
      },
    },
  ];

  const otherColOpt = [
    {
      keyName: 'project',
      title: 'Project',
      isHasDoubleTitle: true,
      width: 240,
      align: 'left',
      fixed: 'left',
      tdOpt: {
        project: {
          studio: 'game',
          src: 'cover',
          title: 'game_name',
          platform: 'platform',
          platformPlaceHolder: '-',
        },
      },
    },
    {
      keyName: 'studio',
      title: 'Studio',
      isHasDoubleTitle: true,
      width: 180,
      align: 'left',
      tdOpt: {
        customize: {
          render: (cvm: any) => {
            return (
              <EllipsisTextBubble
                text={cvm?.studio_name || '-'}
                textElClassName="dbg-hover-active"
                onTextClick={(e) => {
                  e?.stopPropagation();
                  goToStudioDetailsPage({ id: cvm?.studio_id, name: cvm?.studio_name });
                }}
              />
            );
          },
        },
      },
    },
  ];
  const initColOpt = module === 'studio' ? studioColopt : otherColOpt;

  const dates: string[] = []; // 跟页面上对应的降序的日期
  if (
    Array.isArray(data?.list) &&
    data?.list?.length &&
    Array.isArray(data?.list?.[0]?.details) &&
    data?.list?.[0]?.details?.length
  ) {
    // 后端返回的是降序的，不用处理
    const details = data?.list?.[0]?.details;
    for (let i = 0, l = details?.length; i < l; i++) {
      const t = details?.[i];
      dates.push(t?.metric);
    }
  }
  const commonColOpt = [];
  if (dates?.length) {
    for (let i = 0, l = dates?.length; i < l; i++) {
      const t = dates?.[i] || '-';
      commonColOpt.push({
        keyName: t,
        title: `${t === '-' ? '-' : t?.slice(0, 7)}\nGross Revenue($)`,
        isTips: true,
        renderTips: () => {
          return (
            <>
              <p>{CONSTANT_TEXTS.DEFINITION}: Cash Received From Players</p>
              <p>{CONSTANT_TEXTS.DATA_SOURCE}: Reporting Package</p>
              <p>{CONSTANT_TEXTS.UPDATE_FREQUENCY}: Monthly</p>
              <p>
                {CONSTANT_TEXTS.DATA_TIME}: {t === '-' ? '-' : t?.slice(0, 7)}
              </p>
            </>
          );
        },
        isHasDoubleTitle: true,
        width: 206,
        align: 'right',
        tdOpt: getActualTdOpt(t, `${t}_yoy`, `${t}_mom`, yoy, mom),
      });
    }
  }

  return {
    isHasAllLine: module === 'studio',
    columsOptions: [...initColOpt, ...commonColOpt],
    sortableOptions: {
      sortColumns: ['studio', 'project', ...dates],
      defaultSort: {
        by: dates?.[0] || '',
        order: 'desc',
      },
    },
    pageableOptions: {
      pageSizeOptions: [10, 20, 50, 100],
    },
  };
};

export { getTableRecords, getTableOptions };
